/**
 * Token catalog: single source of truth for merge variables.
 * Schema: { key, label, description, group, unique, formatter, exampleValue }
 * Used by VariablePanel for grouped sections and by renderTemplate for formatters.
 */

/** Token definition shape: key, label, token (display e.g. [Client.Name]), group, unique, formatter, exampleValue */
const TOKENS = [
  // Header
  { key: 'facility.name', label: 'Facility Name', token: '[Facility.Name]', group: 'header', unique: false, formatter: null, exampleValue: 'Menlo Atherton Glass' },
  { key: 'facility.address', label: 'Facility Address', token: '[Facility.Address]', group: 'header', unique: false, formatter: null, exampleValue: 'PO Box 5214' },
  { key: 'user.name', label: 'User Name', token: '[User.Name]', group: 'header', unique: false, formatter: null, exampleValue: 'Jane Doe' },
  { key: 'client.name', label: 'Client Name', token: '[Client.Name]', group: 'header', unique: false, formatter: null, exampleValue: 'John Smith' },
  { key: 'client.address', label: 'Client Address', token: '[Client.Address]', group: 'header', unique: false, formatter: null, exampleValue: '123 Main St' },
  { key: 'client.city_state_zip', label: 'City, State, ZIP', token: '[City.State.ZIP]', group: 'header', unique: false, formatter: null, exampleValue: 'San Francisco, CA 94102' },
  { key: 'invoice.number', label: 'Invoice Number', token: '[Invoice.Number]', group: 'header', unique: false, formatter: null, exampleValue: 'INV-001' },
  { key: 'invoice.date', label: 'Invoice Date', token: '[Invoice.Date]', group: 'header', unique: false, formatter: 'date', exampleValue: 'Jan 15, 2024' },
  { key: 'invoice.document_type', label: 'Document Type', token: '[Document.Type]', group: 'header', unique: false, formatter: null, exampleValue: 'INVOICE' },
  { key: 'date', label: 'Date', token: '[Date]', group: 'header', unique: false, formatter: 'date', exampleValue: 'Jan 15, 2024' },
  // Body
  { key: 'invoice.line_items', label: 'Line Items Table', token: '[Line.Items.Table]', group: 'body', unique: true, formatter: null, exampleValue: null },
  { key: 'invoice.items_table', label: 'Items Table', token: '[Items.Table]', group: 'body', unique: true, formatter: null, exampleValue: null },
  { key: 'estimate.items_table', label: 'Items Table (Estimate)', token: '[Items.Table]', group: 'body', unique: true, formatter: null, exampleValue: null },
  { key: 'estimate.line_items', label: 'Line Items (Estimate)', token: '[Line.Items.Table]', group: 'body', unique: true, formatter: null, exampleValue: null },
  { key: 'totals_section', label: 'Totals Section', token: '[Totals.Section]', group: 'body', unique: true, formatter: null, exampleValue: null },
  { key: 'invoice.totals_section', label: 'Totals Section', token: '[Totals.Section]', group: 'body', unique: true, formatter: null, exampleValue: null },
  { key: 'invoice.subtotal', label: 'Subtotal', token: '[Subtotal]', group: 'body', unique: false, formatter: 'currency', exampleValue: '$1,250.00' },
  { key: 'invoice.tax', label: 'Tax', token: '[Tax]', group: 'body', unique: false, formatter: 'currency', exampleValue: '$137.50' },
  { key: 'invoice.total', label: 'Total', token: '[Total]', group: 'body', unique: false, formatter: 'currency', exampleValue: '$1,387.50' },
  // Footer
  { key: 'notes_section', label: 'Notes Section', token: '[Notes.Section]', group: 'footer', unique: true, formatter: null, exampleValue: null },
  { key: 'invoice.notes_section', label: 'Notes Section', token: '[Notes.Section]', group: 'footer', unique: true, formatter: null, exampleValue: null },
  { key: 'invoice.due_date', label: 'Due Date', token: '[Due.Date]', group: 'footer', unique: false, formatter: 'date', exampleValue: 'Feb 15, 2024' },
  { key: 'due_date', label: 'Due Date', token: '[Due.Date]', group: 'footer', unique: false, formatter: 'date', exampleValue: 'Feb 15, 2024' },
  { key: 'client.email', label: 'Client Email', token: '[Client.Email]', group: 'footer', unique: false, formatter: null, exampleValue: 'john@example.com' },
  { key: 'client.phone', label: 'Client Phone', token: '[Client.Phone]', group: 'footer', unique: false, formatter: 'phone', exampleValue: '(555) 123-4567' },
  { key: 'invoice.company_logo', label: 'Company Logo', token: '[Company.Logo]', group: 'header', unique: true, formatter: null, exampleValue: null },
  { key: 'company_logo', label: 'Company Logo', token: '[Company.Logo]', group: 'header', unique: true, formatter: null, exampleValue: null },
];

const GROUPS = ['header', 'body', 'footer'];

/**
 * Get all tokens, optionally filtered by template type and group.
 * @param {string} [templateType] - 'invoice' | 'estimate'
 * @param {string} [group] - 'header' | 'body' | 'footer'
 */
export function getTokens(templateType, group) {
  let list = TOKENS;
  if (templateType === 'estimate') {
    list = list.filter((t) => t.key !== 'invoice.items_table' && t.key !== 'invoice.line_items');
  } else if (templateType === 'invoice') {
    list = list.filter((t) => !t.key.startsWith('estimate.'));
  }
  if (group) list = list.filter((t) => t.group === group);
  return list;
}

/**
 * Get tokens grouped by section for the variable panel.
 */
export function getTokensByGroup(templateType = 'invoice') {
  const tokens = getTokens(templateType);
  const byGroup = { header: [], body: [], footer: [] };
  tokens.forEach((t) => {
    if (byGroup[t.group]) byGroup[t.group].push(t);
  });
  return byGroup;
}

/**
 * Find token by key.
 */
export function getTokenByKey(key) {
  return TOKENS.find((t) => t.key === key);
}

export { TOKENS, GROUPS };
